import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { theme } from '../styles/theme';

export type ColorScheme = 'purple' | 'blue' | 'green' | 'orange';
export type ThemeMode = 'light' | 'dark';

interface ThemeColors {
  gradient: string;
  primary: string;
  button: string;
  accent: string;
  border: string;
  text: string;
  tokens: {
    ctaPrimary: string;
    borderSubtle: string;
    bgPrimaryDark: string;
    chartStroke: string;
    chartGrid: string;
    chartAxisLabels: string;
    chartTooltipBg: string;
    chartTooltipBorder: string;
    textPrimary: string;
    textSecondary: string;
    spacingLg: string;
    spacingMd: string;
    spacingSm: string;
  };
}

interface ThemeContextType {
  colorScheme: ColorScheme;
  setColorScheme: (scheme: ColorScheme) => void;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  colors: ThemeColors;
  cssVars: Record<string, string>;
}

const themeColors: Record<ColorScheme, ThemeColors> = {
  purple: {
    gradient: 'from-purple-400 via-pink-400 to-blue-400',
    primary: 'from-purple-500 to-pink-500',
    button: 'bg-purple-500 hover:bg-purple-600',
    accent: 'text-purple-400',
    border: 'border-purple-500',
    text: 'purple-400',
    tokens: {
      ctaPrimary: theme.colors.primary.blue,
      borderSubtle: '#e5e7eb',
      bgPrimaryDark: theme.colors.background.primary,
      chartStroke: theme.colors.chart.stroke,
      chartGrid: theme.colors.chart.grid,
      chartAxisLabels: theme.colors.chart.axisLabels,
      chartTooltipBg: theme.colors.chart.tooltipBg,
      chartTooltipBorder: theme.colors.chart.tooltipBorder,
      textPrimary: theme.colors.text.primary,
      textSecondary: theme.colors.text.secondary,
      spacingLg: theme.spacing.lg,
      spacingMd: theme.spacing.md,
      spacingSm: theme.spacing.sm,
    }
  },
  blue: {
    gradient: 'from-blue-400 via-cyan-400 to-indigo-400',
    primary: 'from-blue-500 to-cyan-500',
    button: 'bg-blue-500 hover:bg-blue-600',
    accent: 'text-blue-400',
    border: 'border-blue-500',
    text: 'blue-400',
    tokens: {
      ctaPrimary: theme.colors.primary.blue,
      borderSubtle: '#e5e7eb',
      bgPrimaryDark: theme.colors.background.primary,
      chartStroke: theme.colors.chart.stroke,
      chartGrid: theme.colors.chart.grid,
      chartAxisLabels: theme.colors.chart.axisLabels,
      chartTooltipBg: theme.colors.chart.tooltipBg,
      chartTooltipBorder: theme.colors.chart.tooltipBorder,
      textPrimary: theme.colors.text.primary,
      textSecondary: theme.colors.text.secondary,
      spacingLg: theme.spacing.lg,
      spacingMd: theme.spacing.md,
      spacingSm: theme.spacing.sm,
    }
  },
  green: {
    gradient: 'from-green-400 via-emerald-400 to-teal-400',
    primary: 'from-green-500 to-emerald-500',
    button: 'bg-green-500 hover:bg-green-600',
    accent: 'text-green-400',
    border: 'border-green-500',
    text: 'green-400',
    tokens: {
      ctaPrimary: theme.colors.primary.blue,
      borderSubtle: '#e5e7eb',
      bgPrimaryDark: theme.colors.background.primary,
      chartStroke: theme.colors.chart.stroke,
      chartGrid: theme.colors.chart.grid,
      chartAxisLabels: theme.colors.chart.axisLabels,
      chartTooltipBg: theme.colors.chart.tooltipBg,
      chartTooltipBorder: theme.colors.chart.tooltipBorder,
      textPrimary: theme.colors.text.primary,
      textSecondary: theme.colors.text.secondary,
      spacingLg: theme.spacing.lg,
      spacingMd: theme.spacing.md,
      spacingSm: theme.spacing.sm,
    }
  },
  orange: {
    gradient: 'from-orange-400 via-red-400 to-pink-400',
    primary: 'from-orange-500 to-red-500',
    button: 'bg-orange-500 hover:bg-orange-600',
    accent: 'text-orange-400',
    border: 'border-orange-500',
    text: 'orange-400',
    tokens: {
      ctaPrimary: theme.colors.primary.blue,
      borderSubtle: '#e5e7eb',
      bgPrimaryDark: theme.colors.background.primary,
      chartStroke: theme.colors.chart.stroke,
      chartGrid: theme.colors.chart.grid,
      chartAxisLabels: theme.colors.chart.axisLabels,
      chartTooltipBg: theme.colors.chart.tooltipBg,
      chartTooltipBorder: theme.colors.chart.tooltipBorder,
      textPrimary: theme.colors.text.primary,
      textSecondary: theme.colors.text.secondary,
      spacingLg: theme.spacing.lg,
      spacingMd: theme.spacing.md,
      spacingSm: theme.spacing.sm,
    }
  }
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>('blue');
  const [themeMode, setThemeModeState] = useState<ThemeMode>('dark');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('loopletai-theme') as ColorScheme;
    const savedMode = localStorage.getItem('loopletai-theme-mode') as ThemeMode;
    if (savedTheme && themeColors[savedTheme]) {
      setColorSchemeState(savedTheme);
    }
    if (savedMode) {
      setThemeModeState(savedMode);
    }
  }, []);

  const setColorScheme = (scheme: ColorScheme) => {
    setColorSchemeState(scheme);
    localStorage.setItem('loopletai-theme', scheme);
  };

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('loopletai-theme-mode', mode);
  };

  const colors = themeColors[colorScheme];
  
  // Generate CSS custom properties
  const cssVars = {
    '--color-primary': colors.tokens.ctaPrimary,
    '--color-text-primary': colors.tokens.textPrimary,
    '--color-text-secondary': colors.tokens.textSecondary,
    '--color-bg-primary': colors.tokens.bgPrimaryDark,
    '--color-bg-secondary': theme.colors.background.secondary,
    '--color-border-primary': themeMode === 'dark' ? theme.colors.border.main : '#e5e7eb',
    '--color-gray-700': theme.colors.chart.gridDark,
    '--color-gray-500': theme.colors.border.light,
    '--chart-stroke': colors.tokens.chartStroke,
    '--chart-grid': themeMode === 'dark' ? theme.colors.chart.gridDark : theme.colors.chart.gridLight,
    '--chart-axis-labels': colors.tokens.chartAxisLabels,
    '--chart-tooltip-bg': colors.tokens.chartTooltipBg,
    '--chart-tooltip-border': colors.tokens.chartTooltipBorder,
    '--space-sm': colors.tokens.spacingSm,
    '--space-md': colors.tokens.spacingMd,
    '--space-lg': colors.tokens.spacingLg,
    '--space-xl': theme.spacing.xl,
  };

  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    // Set data-theme attribute for theme-aware styling
    root.setAttribute('data-theme', themeMode);
  }, [cssVars, themeMode]);

  return (
    <ThemeContext.Provider value={{ colorScheme, setColorScheme, themeMode, setThemeMode, colors, cssVars }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};